/**
 * useSeo — данные SEO-раздела (docs/seo-section-tz.md). Cache-first через React Query.
 * Один файл на раздел (как useCompetitors): типы отражают контракт server/src/routes/seo.ts.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';

export type Engine = 'yandex' | 'google' | 'both';
export type Confidence = 'Confirmed' | 'Likely' | 'Hypothesis';
export type Impact = 'high' | 'med' | 'low';
export type Effort = 'S' | 'M' | 'L';

export interface SeoCluster {
  id: number;
  name: string;
  head_keyword: string;
  intent: string;
  funnel_stage: string;
  persona: string;
  page_type: string;
  volume: number;
  engine: Engine;
  serp_competitors: unknown;
  priority: number;
  target_url: string | null;
  page_id: number | null;
  status: string;
  updated_at: string;
}

export interface SeoTask {
  id: number;
  source_module: string;
  title: string;
  description: string;
  evidence: unknown;
  confidence: Confidence;
  impact: Impact;
  effort: Effort;
  engine: Engine;
  status: string;
  ref_page_id: number | null;
  ref_cluster_id: number | null;
  approved_by: string | null;
  approved_at: string | null;
  created_at: string;
}

export interface SeoPage {
  id: number;
  url: string;
  title: string;
  cluster_id: number | null;
  page_type: string;
  status: string;
  published_at: string | null;
  meta: Record<string, unknown>;
  updated_at: string;
}

export interface SeoAudit {
  id: number;
  kind: string;
  target_url: string;
  engine: Engine;
  score: number | null;
  verdict: { veto?: boolean; vetoReasons?: string[]; score?: number; color?: string } | null;
  findings: unknown;
  confidence: Confidence;
  created_at: string;
}

export interface SeoCost {
  id: number;
  period_month: string;
  category: string;
  amount: number;
  note: string | null;
  created_by: string;
}

export interface MeasureResult {
  configured: boolean;
  metrikaConfigured: boolean;
  attribution: 'full' | 'partial';
  cplCap: number;
  mqlTotal: number;
  mqlForms: number;
  mqlCalls: number;
  callsConfigured: boolean;
  bitrixOrganicDeals: number | null;
  byEngine: { yandex: number; google: number };
  sessions: { yandex: number; google: number };
  byPage: { url: string; mql: number }[];
  costs: { month: string; total: number; byCategory: Record<string, number> };
  directCpl: number | null;
  directCplNote: string | null;
  maturingPages: number;
  maturePages: number;
}

export interface PositionsByEngine {
  tracked: number;
  top3: number;
  top10: number;
  avg: number | null;
}
export interface PositionsSnapshot {
  configured: boolean;
  byEngine: { yandex: PositionsByEngine; google: PositionsByEngine };
  generatedAt: string;
}
export interface PositionsResponse {
  latest: PositionsSnapshot | null;
  series: { takenAt: string; data: PositionsSnapshot }[];
}

export interface OrganicCompetitor {
  domain: string;
  name: string;
  category: string;
  orgTraf: number;
  adTraf: number;
}
export interface CompetitorsOrganicResponse {
  configured: boolean;
  generatedAt: string;
  competitors: OrganicCompetitor[];
}

export interface SeoOverview {
  configured: boolean;
  metrikaConfigured: boolean;
  stats: { pagesByStatus: Record<string, number>; tasksOpen: number; tasksApproved: number };
  gapsCount: number;
  topTasks: SeoTask[];
  traffic: { sessions: { yandex: number; google: number }; source?: string; period?: string } | null;
  mql: { mqlForms: number; byEngine: { yandex: number; google: number } } | null;
}

// ─────────────── Обзор (B1) ───────────────
export function useSeoOverview() {
  return useQuery<SeoOverview>({
    queryKey: ['seo', 'overview'],
    queryFn: () => apiFetch<SeoOverview>('/api/seo/overview'),
    staleTime: 60_000,
  });
}

// ─────────────── Кластеры (A1) ───────────────
// poll=true — опрос каждые 4с (пока AI собирает ядро в фоне).
export function useSeoClusters(filters: { status?: string; engine?: string } = {}, poll = false) {
  const qs = new URLSearchParams();
  if (filters.status) qs.set('status', filters.status);
  if (filters.engine) qs.set('engine', filters.engine);
  const suffix = qs.toString() ? `?${qs}` : '';
  return useQuery<{ clusters: SeoCluster[] }>({
    queryKey: ['seo', 'clusters', filters],
    queryFn: () => apiFetch(`/api/seo/clusters${suffix}`),
    staleTime: 60_000,
    refetchInterval: poll ? 4000 : false,
  });
}

export function useSeoGaps(poll = false) {
  return useQuery<{ gaps: SeoCluster[] }>({
    queryKey: ['seo', 'gaps'],
    queryFn: () => apiFetch('/api/seo/clusters/gaps'),
    staleTime: 60_000,
    refetchInterval: poll ? 4000 : false,
  });
}

/** Ответ асинхронного build: status='started' (AI в фоне) | 'done' (создано сразу). */
export interface BuildResponse {
  status: 'started' | 'done';
  via: 'ai' | 'seeds' | 'payload';
  created?: number;
}

export function useSeoMutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['seo'] });
  };
  const buildClusters = useMutation({
    mutationFn: (body: { seeds?: string[]; clusters?: Partial<SeoCluster>[] }) =>
      apiFetch<BuildResponse>('/api/seo/clusters/build', { method: 'POST', json: body }),
    onSuccess: invalidate,
  });
  const createCluster = useMutation({
    mutationFn: (body: Partial<SeoCluster>) => apiFetch('/api/seo/clusters', { method: 'POST', json: body }),
    onSuccess: invalidate,
  });
  const patchCluster = useMutation({
    mutationFn: ({ id, ...body }: { id: number } & Partial<SeoCluster>) =>
      apiFetch(`/api/seo/clusters/${id}`, { method: 'PATCH', json: body }),
    onSuccess: invalidate,
  });
  const createTask = useMutation({
    mutationFn: (body: Partial<SeoTask>) => apiFetch('/api/seo/tasks', { method: 'POST', json: body }),
    onSuccess: invalidate,
  });
  const patchTask = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      apiFetch(`/api/seo/tasks/${id}`, { method: 'PATCH', json: { status } }),
    onSuccess: invalidate,
  });
  const runCommercialAudit = useMutation({
    mutationFn: (body: { target_url: string; findings?: unknown[]; veto?: string[] }) =>
      apiFetch<{ status?: string }>('/api/seo/audit/commercial', { method: 'POST', json: body }),
    onSuccess: invalidate,
  });
  const createPage = useMutation({
    mutationFn: (body: Partial<SeoPage>) => apiFetch('/api/seo/pages', { method: 'POST', json: body }),
    onSuccess: invalidate,
  });
  const patchPage = useMutation({
    mutationFn: ({ id, ...body }: { id: number } & Record<string, unknown>) =>
      apiFetch(`/api/seo/pages/${id}`, { method: 'PATCH', json: body }),
    onSuccess: invalidate,
  });
  const textcheck = useMutation({
    mutationFn: ({ id, text }: { id: number; text: string }) =>
      apiFetch<{ configured: boolean; passed: boolean; risk: number | null; readability: number | null; findings: string[] }>(
        `/api/seo/pages/${id}/textcheck`,
        { method: 'POST', json: { text } },
      ),
    onSuccess: invalidate,
  });
  const createBrief = useMutation({
    mutationFn: (body: { pageId?: number; title: string; body: string }) =>
      apiFetch('/api/seo/briefs', { method: 'POST', json: body }),
    onSuccess: invalidate,
  });
  const createCost = useMutation({
    mutationFn: (body: { period_month: string; category?: string; amount: number; note?: string }) =>
      apiFetch('/api/seo/costs', { method: 'POST', json: body }),
    onSuccess: invalidate,
  });
  const deleteCost = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/seo/costs/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
  return {
    buildClusters, createCluster, patchCluster, createTask, patchTask,
    runCommercialAudit, createPage, patchPage, textcheck, createBrief, createCost, deleteCost,
  };
}

// ─────────────── Аудиты (C3) ───────────────
export function useSeoAudits(kind?: string, poll = false) {
  const suffix = kind ? `?kind=${kind}` : '';
  return useQuery<{ audits: SeoAudit[] }>({
    queryKey: ['seo', 'audits', kind ?? 'all'],
    queryFn: () => apiFetch(`/api/seo/audits${suffix}`),
    staleTime: 60_000,
    refetchInterval: poll ? 4000 : false,
  });
}

// ─────────────── Контент (D1) ───────────────
export function useSeoPages() {
  return useQuery<{ pages: SeoPage[] }>({
    queryKey: ['seo', 'pages'],
    queryFn: () => apiFetch('/api/seo/pages'),
    staleTime: 60_000,
  });
}

// ─────────────── Задачи (E1) ───────────────
export function useSeoTasks(filters: { status?: string; engine?: string; source_module?: string } = {}) {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) if (v) qs.set(k, v);
  const suffix = qs.toString() ? `?${qs}` : '';
  return useQuery<{ tasks: SeoTask[] }>({
    queryKey: ['seo', 'tasks', filters],
    queryFn: () => apiFetch(`/api/seo/tasks${suffix}`),
    staleTime: 30_000,
  });
}

// ─────────────── Замер (B3) ───────────────
export function useSeoMeasure() {
  return useQuery<MeasureResult>({
    queryKey: ['seo', 'measure'],
    queryFn: () => apiFetch('/api/seo/measure'),
    staleTime: 60_000,
  });
}

export function useSeoCosts() {
  return useQuery<{ costs: SeoCost[] }>({
    queryKey: ['seo', 'costs'],
    queryFn: () => apiFetch('/api/seo/costs'),
    staleTime: 60_000,
  });
}

// ─────────────── Позиции (B2) ───────────────
export function useSeoPositions() {
  return useQuery<PositionsResponse>({
    queryKey: ['seo', 'positions'],
    queryFn: () => apiFetch('/api/seo/positions'),
    staleTime: 5 * 60_000,
  });
}

// ─────────────── Органик-конкуренты (A2) ───────────────
export function useSeoCompetitorsOrganic() {
  return useQuery<CompetitorsOrganicResponse>({
    queryKey: ['seo', 'competitors-organic'],
    queryFn: () => apiFetch('/api/seo/competitors-organic'),
    staleTime: 5 * 60_000,
  });
}

// ─────────────── Мутации Фазы 2/3 ───────────────
export function useSeoPhase2Mutations() {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['seo'] });
  };
  const refreshPositions = useMutation({
    mutationFn: () => apiFetch('/api/seo/positions/refresh', { method: 'POST' }),
    onSuccess: invalidate,
  });
  const refreshCompetitorsOrganic = useMutation({
    mutationFn: () => apiFetch('/api/seo/competitors-organic/refresh', { method: 'POST' }),
    onSuccess: invalidate,
  });
  const runTechnicalAudit = useMutation({
    mutationFn: () => apiFetch('/api/seo/audit/technical', { method: 'POST' }),
    onSuccess: invalidate,
  });
  const runOnpageAudit = useMutation({
    mutationFn: (target_url: string) => apiFetch<{ status?: string }>('/api/seo/audit/onpage', { method: 'POST', json: { target_url } }),
    onSuccess: invalidate,
  });
  return { refreshPositions, refreshCompetitorsOrganic, runTechnicalAudit, runOnpageAudit };
}
